/**
 * Assembling everything the browser knows, at the moment a report is sent.
 *
 * Collected on submit rather than kept up to date, so the snapshot is of the
 * screen the reporter was actually looking at when they decided to say
 * something.
 *
 * Every field here is observable from the page. That is the test applied to
 * each one, and it is why the reporter can be shown the whole list before they
 * send it: nothing in here tells a developer anything the reporter could not
 * have read out loud - it just saves the phone call.
 */
import type { ClientDiagnostics, ReconciliationResult, RunSnapshot } from "@/shared/api";
import { logSnapshot } from "./logBuffer";

/**
 * The commit this bundle was built from.
 *
 * Set by the frontend deploy as `VITE_BUILD_SHA`, and absent in a local `vite`
 * run. It answers the question a stale cache raises: whether the reporter is
 * even running the code being read. The API's own revision comes back on
 * `/api/config` and is added to the issue server-side, so a mismatch between
 * the two is visible - which is its own kind of bug report.
 */
const CLIENT_REVISION: string | null = import.meta.env.VITE_BUILD_SHA ?? null;

/**
 * Reduces a reconciliation to counts and parameters.
 *
 * @param result - The loaded run, or null if there isn't one.
 * @returns The snapshot, or null. Never any line, order or receipt - see
 *   {@link RunSnapshot}.
 */
export function summarizeRun(result: ReconciliationResult | null): RunSnapshot | null {
  if (!result) return null;

  return {
    // Stringified because the API models this as a map of strings: the values
    // are a mix of numbers, dates and labels, and they exist to be read rather
    // than computed with.
    parameters: Object.fromEntries(
      Object.entries(result.parameters).map(([key, value]) => [key, String(value)]),
    ),
    source_rows: result.totals.source_rows,
    costed_lines: result.totals.costed_lines,
    purchase_orders: result.totals.purchase_orders,
    flag_count: result.totals.flag_count,
  };
}

/** Reads the browser's time zone, which is not guaranteed to be available. */
function timezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown";
  } catch {
    return "unknown";
  }
}

/**
 * Collects the diagnostics to send with a report.
 *
 * @param route - Path the reporter is on, from the router rather than
 *   `location.pathname`, so it is the route the app thinks it is showing.
 * @param result - The reconciliation on screen, if any.
 * @returns The payload, matching the API's `ClientDiagnostics`.
 */
export function collectDiagnostics(
  route: string,
  result: ReconciliationResult | null,
): ClientDiagnostics {
  return {
    route,
    client_revision: CLIENT_REVISION,
    // The raw string, not a prettied "Chrome on Windows". A developer can read
    // this one and cannot recover what a summary threw away.
    user_agent: navigator.userAgent.slice(0, 400),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    timezone: timezone(),
    reported_at: new Date().toISOString(),
    logs: logSnapshot(),
    run: summarizeRun(result),
  };
}
